import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, ComposedChart, Line, ReferenceLine
} from "recharts";

// ── Maghil Design Tokens ──
const T = {
  bg: "#F5F6FA", surface: "#FFFFFF", card: "#FFFFFF", cardBorder: "#E2E5EC",
  hover: "#F0F1F5", accent: "#D23A3B", accentSoft: "rgba(210,58,59,0.08)",
  accentGlow: "rgba(210,58,59,0.15)", green: "#16A34A", greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)", orange: "#D97706",
  orangeSoft: "rgba(217,119,6,0.08)", t1: "#1A1D2E", t2: "#5A6178", t3: "#9CA3B8",
  divider: "#E8EAF0", shadow: "0 1px 3px rgba(0,0,0,0.06)",
  cardShadow: "0 1px 4px rgba(0,0,0,0.05)", sidebarShadow: "2px 0 8px rgba(0,0,0,0.03)",
  topbarShadow: "0 1px 3px rgba(0,0,0,0.04)",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const MIcon = ({ name, size = 18, color = T.t3, fill = false, style: sx = {} }: any) => (
  <span className="material-symbols-outlined" style={{
    fontSize: size, color,
    fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' ${size > 20 ? 24 : 20}`,
    lineHeight: 1, userSelect: "none", ...sx,
  }}>{name}</span>
);

// ── Mock Data ──
const kpiData = [
  { label: "TOTAL HOURS", value: "482.5", delta: "+8.2%", deltaDir: "up", icon: "schedule", drillId: "hours" },
  { label: "SPLH", value: "$38.72", delta: "+5.1%", deltaDir: "up", icon: "speed", drillId: "splh" },
  { label: "TOTAL TIPS", value: "$6,842", delta: "+12.4%", deltaDir: "up", icon: "payments", drillId: "tips" },
  { label: "ORDERS / LABOR HR", value: "4.8", delta: "+3.2%", deltaDir: "up", icon: "package_2", drillId: null },
  { label: "VOID RATE", value: "1.2%", delta: "-0.4%", deltaDir: "down", icon: "block", drillId: "voids" },
];

const hourlyData = [
  { hour: "6AM", sales: 420, staff: 3 }, { hour: "7AM", sales: 680, staff: 4 },
  { hour: "8AM", sales: 1240, staff: 6 }, { hour: "9AM", sales: 1580, staff: 7 },
  { hour: "10AM", sales: 1320, staff: 7 }, { hour: "11AM", sales: 2450, staff: 9 },
  { hour: "12PM", sales: 3680, staff: 12 }, { hour: "1PM", sales: 3420, staff: 12 },
  { hour: "2PM", sales: 2180, staff: 10 }, { hour: "3PM", sales: 1420, staff: 7 },
  { hour: "4PM", sales: 1680, staff: 8 }, { hour: "5PM", sales: 2840, staff: 10 },
  { hour: "6PM", sales: 3520, staff: 12 }, { hour: "7PM", sales: 3780, staff: 13 },
  { hour: "8PM", sales: 3240, staff: 12 }, { hour: "9PM", sales: 2180, staff: 9 },
  { hour: "10PM", sales: 1020, staff: 5 },
];
const hourlySplh = hourlyData.map(h => ({ ...h, splh: h.staff > 0 ? +(h.sales / h.staff).toFixed(1) : 0 }));

const splhTrend = [
  { day: "Mon", splh: 34.2 }, { day: "Tue", splh: 36.8 }, { day: "Wed", splh: 38.1 },
  { day: "Thu", splh: 41.2 }, { day: "Fri", splh: 44.6 }, { day: "Sat", splh: 42.3 }, { day: "Sun", splh: 38.7 },
];

const voidReasons = [
  { name: "Customer Request", value: 42, color: T.accent },
  { name: "Kitchen Error", value: 28, color: T.orange },
  { name: "Wrong Item", value: 18, color: T.red },
  { name: "Quality Issue", value: 12, color: T.t3 },
];

const staffData = [
  { name: "Maria Santos", role: "Server", hours: 38.5, sales: 4820, tips: 682, orders: 96, voids: 45, splh: 125.19, cancelRate: 1.0, clockIn: "6:00 AM", clockOut: "2:30 PM", breakHrs: 1.0 },
  { name: "James Wilson", role: "Server", hours: 36.0, sales: 4210, tips: 594, orders: 88, voids: 120, splh: 116.94, cancelRate: 2.3, clockIn: "10:00 AM", clockOut: "6:00 PM", breakHrs: 1.0 },
  { name: "Ashley Chen", role: "Bartender", hours: 34.5, sales: 3980, tips: 720, orders: 142, voids: 30, splh: 115.36, cancelRate: 0.7, clockIn: "4:00 PM", clockOut: "12:30 AM", breakHrs: 0.5 },
  { name: "David Kim", role: "Server", hours: 40.0, sales: 3650, tips: 485, orders: 78, voids: 85, splh: 91.25, cancelRate: 3.8, clockIn: "8:00 AM", clockOut: "5:00 PM", breakHrs: 1.0 },
  { name: "Sarah Johnson", role: "Host", hours: 32.0, sales: 1240, tips: 180, orders: 45, voids: 0, splh: 38.75, cancelRate: 0.0, clockIn: "11:00 AM", clockOut: "7:00 PM", breakHrs: 1.0 },
  { name: "Carlos Rivera", role: "Server", hours: 28.5, sales: 3180, tips: 412, orders: 72, voids: 60, splh: 111.58, cancelRate: 1.4, clockIn: "5:00 PM", clockOut: "11:30 PM", breakHrs: 0.5 },
  { name: "Priya Patel", role: "Bartender", hours: 30.0, sales: 3420, tips: 548, orders: 118, voids: 25, splh: 114.00, cancelRate: 0.8, clockIn: "3:00 PM", clockOut: "11:00 PM", breakHrs: 0.5 },
];

const voidLog = [
  { id: "V-1042", staff: "David Kim", item: "Wagyu Burger", amount: 34, reason: "Customer Request", time: "7:42 PM", date: "Mar 9" },
  { id: "V-1041", staff: "James Wilson", item: "Truffle Fries", amount: 18, reason: "Kitchen Error", time: "6:18 PM", date: "Mar 9" },
  { id: "V-1039", staff: "David Kim", item: "Caesar Salad", amount: 16, reason: "Wrong Item", time: "5:05 PM", date: "Mar 9" },
  { id: "V-1038", staff: "Carlos Rivera", item: "Lobster Roll", amount: 28, reason: "Quality Issue", time: "1:22 PM", date: "Mar 8" },
  { id: "V-1035", staff: "James Wilson", item: "Pasta Primavera", amount: 22, reason: "Kitchen Error", time: "12:40 PM", date: "Mar 8" },
  { id: "V-1033", staff: "David Kim", item: "NY Strip Steak", amount: 42, reason: "Customer Request", time: "8:15 PM", date: "Mar 7" },
  { id: "V-1031", staff: "Ashley Chen", item: "Espresso Martini", amount: 16, reason: "Wrong Item", time: "9:30 PM", date: "Mar 7" },
];

const hoursBreakdown = [
  { day: "Mon", regular: 62, breaks: 8 }, { day: "Tue", regular: 68, breaks: 9 },
  { day: "Wed", regular: 72, breaks: 10 }, { day: "Thu", regular: 74, breaks: 10 },
  { day: "Fri", regular: 82, breaks: 12 }, { day: "Sat", regular: 78, breaks: 11 },
  { day: "Sun", regular: 46, breaks: 6 },
];

const navGroups = [
  { icon: "bar_chart", label: "Sales", items: ["Sales Summary", "Hourly Sales", "Order Channels"] },
  { icon: "restaurant", label: "Menu", items: ["Item Sales", "Category Sales", "Discounts & Offers"] },
  { icon: "groups", label: "Team", items: ["Staff Performance", "Labor & Workforce", "Voids & Exceptions", "Refunds"] },
  { icon: "account_balance_wallet", label: "Payments", items: ["Tender Types", "EOD Reconciliation"] },
  { icon: "event_seat", label: "Guests", items: ["Check-In Analytics", "Party & Wait Times"] },
  { icon: "location_on", label: "Locations", items: ["Location Benchmark"] },
];
const datePresets = ["Today", "Yesterday", "This Week", "Last 7 Days", "This Month"];

// ── Reusable Components ──
const DrillArrow = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 8, right: 8, opacity: 0.45 }}>
    <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronSvg = ({ open }: any) => (
  <svg width="10" height="10" viewBox="0 0 10 10" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
    <path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={T.t3} strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

function KPICard({ label, value, delta, deltaDir, icon, drillId, onDrill }: any) {
  const isPositiveContext = label === "VOID RATE";
  const isGood = isPositiveContext ? deltaDir === "down" : deltaDir === "up";
  const deltaColor = isGood ? T.green : T.red;
  const softColor = isGood ? T.greenSoft : T.redSoft;
  const arrow = deltaDir === "up" ? "↑" : "↓";
  const hasDrill = !!drillId;

  return (
    <div onClick={() => hasDrill && onDrill(drillId)} style={{
      flex: 1, minWidth: 160, background: T.card,
      border: hasDrill ? `1px solid rgba(210,58,59,0.2)` : `1px solid ${T.cardBorder}`,
      borderRadius: 14, padding: "20px 22px", boxShadow: T.cardShadow,
      position: "relative", overflow: "hidden", cursor: hasDrill ? "pointer" : "default",
    }}>
      <div style={{
        position: "absolute", top: -10, right: -10, width: 80, height: 80,
        borderRadius: "50%", background: softColor, filter: "blur(20px)",
      }} />
      {hasDrill && <DrillArrow />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: T.font }}>{label}</span>
        <MIcon name={icon} size={20} color={T.t3} />
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, fontFamily: T.font }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor, marginTop: 6, fontFamily: T.font }}>
        {arrow} {delta} <span style={{ fontWeight: 400, color: T.t3 }}>vs prior period</span>
      </div>
    </div>
  );
}

function SidebarNavGroup({ icon, label, items, activeItem, onSelect }: any) {
  const [open, setOpen] = useState(label === "Team");
  return (
    <div style={{ marginBottom: 4 }}>
      <div onClick={() => setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", cursor: "pointer", userSelect: "none", borderRadius: 6 }}>
        <MIcon name={icon} size={18} color={open ? T.accent : T.t3} fill={open} />
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: open ? T.accent : T.t2, fontFamily: T.font }}>{label}</span>
        <ChevronSvg open={open} />
      </div>
      {open && <div style={{ paddingLeft: 12 }}>{items.map(item => {
        const isActive = item === activeItem;
        return (<div key={item} onClick={() => onSelect(item)} style={{
          padding: "8px 10px 8px 22px", fontSize: 12, fontFamily: T.font,
          fontWeight: isActive ? 600 : 400, color: isActive ? T.accent : T.t2,
          background: isActive ? T.accentSoft : "transparent",
          borderLeft: isActive ? `2px solid ${T.accent}` : "2px solid transparent",
          borderRadius: "0 6px 6px 0", cursor: "pointer", marginBottom: 1, transition: "all 0.15s",
        }}>{item}</div>);
      })}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "10px 14px", boxShadow: T.cardShadow, fontFamily: T.font }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.t1, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 11, color: T.t2, display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ width: 6, height: 6, borderRadius: 2, background: p.color || p.stroke }} />
          {p.name}: <span style={{ fontWeight: 600, color: T.t1 }}>{typeof p.value === "number" && p.name !== "Staff" ? (p.name === "SPLH" ? `$${p.value}` : `$${p.value.toLocaleString()}`) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

function DataTable({ columns, rows, defaultSort, defaultDir = "desc" }: any) {
  const [sortCol, setSortCol] = useState(defaultSort);
  const [sortDir, setSortDir] = useState(defaultDir);
  const sorted = [...rows].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol];
    if (typeof av === "number") return sortDir === "desc" ? bv - av : av - bv;
    return sortDir === "desc" ? String(bv).localeCompare(String(av)) : String(av).localeCompare(String(bv));
  });
  const handleSort = (col: any) => { if (sortCol === col) setSortDir(d => d === "desc" ? "asc" : "desc"); else { setSortCol(col); setSortDir("desc"); } };

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font }}>
      <thead><tr style={{ borderBottom: `1px solid ${T.divider}` }}>
        {columns.map(col => (
          <th key={col.key} onClick={() => col.sortable !== false && handleSort(col.key)} style={{
            textAlign: col.align || "right", padding: "10px 8px", fontSize: 11, fontWeight: 600,
            color: sortCol === col.key ? T.accent : T.t3, textTransform: "uppercase",
            cursor: col.sortable !== false ? "pointer" : "default", userSelect: "none", letterSpacing: 0.3,
          }}>{col.label} {sortCol === col.key ? (sortDir === "desc" ? "↓" : "↑") : ""}</th>
        ))}
      </tr></thead>
      <tbody>{sorted.map((row, ri) => (
        <tr key={ri} style={{ borderBottom: `1px solid ${T.divider}`, transition: "background 0.1s" }}
          onMouseEnter={e => e.currentTarget.style.background = T.hover}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
          {columns.map(col => (
            <td key={col.key} style={{ textAlign: col.align || "right", padding: "10px 8px", fontSize: 12, color: T.t1 }}>
              {col.render ? col.render(row[col.key], row) : row[col.key]}
            </td>
          ))}
        </tr>
      ))}</tbody>
    </table>
  );
}

// ── Drill-Down Screens ──

function DrillHours({ onBack }: any) {
  return (<>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
      <div onClick={onBack} style={{ width: 32, height: 32, borderRadius: 8, background: T.hover, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <MIcon name="arrow_back" size={18} color={T.t2} />
      </div>
      <div>
        <div style={{ fontSize: 12, color: T.t3, fontFamily: T.font }}>Labor & Workforce</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, fontFamily: T.font }}>Total Hours Breakdown</div>
      </div>
    </div>
    {/* Summary mini KPIs */}
    <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
      {[{ l: "TOTAL HOURS", v: "482.5", i: "schedule" }, { l: "BREAK HOURS", v: "66.0", i: "coffee" }, { l: "GROSS HOURS", v: "548.5", i: "timer" }, { l: "AVG HRS/STAFF", v: "68.9", i: "person" }].map(k => (
        <div key={k.l} style={{ flex: 1, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: "16px 20px", boxShadow: T.cardShadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: T.font }}>{k.l}</span>
            <MIcon name={k.i} size={18} color={T.t3} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.t1, fontFamily: T.font }}>{k.v}</div>
        </div>
      ))}
    </div>
    {/* Daily Hours Chart */}
    <div style={{ background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}`, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <MIcon name="bar_chart" size={18} color={T.t2} />
        <span style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Daily Hours (Regular + Breaks)</span>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={hoursBreakdown}><CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={{ stroke: T.divider }} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="regular" name="Regular Hrs" stackId="h" fill={T.accent} barSize={24} />
          <Bar dataKey="breaks" name="Break Hrs" stackId="h" fill={T.t3} opacity={0.4} radius={[3, 3, 0, 0]} barSize={24} />
        </BarChart>
      </ResponsiveContainer>
    </div>
    {/* Staff Timecard Table */}
    <div style={{ background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <MIcon name="table_chart" size={18} color={T.t2} />
        <span style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Staff Timecard Detail</span>
      </div>
      <DataTable defaultSort="hours" columns={[
        { key: "name", label: "Staff Name", align: "left", sortable: false, render: (v, r) => (<div><div style={{ fontSize: 12, fontWeight: 600, color: T.accent }}>{v}</div><div style={{ fontSize: 10, color: T.t3 }}>{r.role}</div></div>) },
        { key: "clockIn", label: "Clock In", render: v => v },
        { key: "clockOut", label: "Clock Out", render: v => v },
        { key: "hours", label: "Hours", render: v => v.toFixed(1) },
        { key: "breakHrs", label: "Break Hrs", render: v => v.toFixed(1) },
      ]} rows={staffData} />
    </div>
  </>);
}

function DrillSplh({ onBack }: any) {
  return (<>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
      <div onClick={onBack} style={{ width: 32, height: 32, borderRadius: 8, background: T.hover, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <MIcon name="arrow_back" size={18} color={T.t2} />
      </div>
      <div>
        <div style={{ fontSize: 12, color: T.t3, fontFamily: T.font }}>Labor & Workforce</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, fontFamily: T.font }}>SPLH Detail</div>
      </div>
    </div>
    <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
      {[{ l: "AVG SPLH", v: "$38.72", i: "speed" }, { l: "PEAK SPLH", v: "$44.60", i: "trending_up" }, { l: "LOW SPLH", v: "$34.20", i: "trending_down" }].map(k => (
        <div key={k.l} style={{ flex: 1, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: "16px 20px", boxShadow: T.cardShadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: T.font }}>{k.l}</span>
            <MIcon name={k.i} size={18} color={T.t3} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.t1, fontFamily: T.font }}>{k.v}</div>
        </div>
      ))}
    </div>
    {/* Hourly SPLH dual-axis */}
    <div style={{ background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}`, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <MIcon name="stacked_line_chart" size={18} color={T.t2} />
        <span style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Hourly Sales vs. Staff Count vs. SPLH</span>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={hourlySplh}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
          <XAxis dataKey="hour" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={{ stroke: T.divider }} tickLine={false} />
          <YAxis yAxisId="sales" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(1)}k`} />
          <YAxis yAxisId="staff" orientation="right" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} />
          <Bar yAxisId="sales" dataKey="sales" name="Sales" fill={T.accent} radius={[3, 3, 0, 0]} barSize={18} opacity={0.7} />
          <Line yAxisId="staff" type="monotone" dataKey="staff" name="Staff" stroke={T.green} strokeWidth={2} dot={{ r: 3, fill: T.green, stroke: "#fff", strokeWidth: 2 }} />
          <Line yAxisId="staff" type="monotone" dataKey="splh" name="SPLH" stroke={T.orange} strokeWidth={2} strokeDasharray="4 3" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
    {/* Hourly table */}
    <div style={{ background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <MIcon name="table_chart" size={18} color={T.t2} />
        <span style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Hourly Breakdown</span>
      </div>
      <DataTable defaultSort="sales" columns={[
        { key: "hour", label: "Hour", align: "left", sortable: false, render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
        { key: "sales", label: "Sales", render: v => `$${v.toLocaleString()}` },
        { key: "staff", label: "Staff Count", render: v => v },
        { key: "splh", label: "SPLH", render: v => <span style={{ color: v >= 35 ? T.green : T.red, fontWeight: 600 }}>${v.toFixed(0)}</span> },
      ]} rows={hourlySplh} />
    </div>
  </>);
}

function DrillTips({ onBack }: any) {
  const tipDist = [{ range: "$0–10", count: 8 }, { range: "$10–15", count: 22 }, { range: "$15–20", count: 35 }, { range: "$20–30", count: 28 }, { range: "$30+", count: 12 }];
  return (<>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
      <div onClick={onBack} style={{ width: 32, height: 32, borderRadius: 8, background: T.hover, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <MIcon name="arrow_back" size={18} color={T.t2} />
      </div>
      <div>
        <div style={{ fontSize: 12, color: T.t3, fontFamily: T.font }}>Labor & Workforce</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, fontFamily: T.font }}>Tip Breakdown</div>
      </div>
    </div>
    <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
      {[{ l: "TOTAL TIPS", v: "$6,842", i: "payments" }, { l: "AVG TIP/HR", v: "$14.18", i: "schedule" }, { l: "TOP EARNER", v: "Ashley C.", i: "emoji_events" }].map(k => (
        <div key={k.l} style={{ flex: 1, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: "16px 20px", boxShadow: T.cardShadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: T.font }}>{k.l}</span>
            <MIcon name={k.i} size={18} color={T.t3} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.t1, fontFamily: T.font }}>{k.v}</div>
        </div>
      ))}
    </div>
    {/* Tip distribution histogram */}
    <div style={{ background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}`, marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <MIcon name="bar_chart" size={18} color={T.t2} />
        <span style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Tip Distribution (per order)</span>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={tipDist}><CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
          <XAxis dataKey="range" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={{ stroke: T.divider }} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="count" name="Orders" fill={T.green} radius={[3, 3, 0, 0]} barSize={32} opacity={0.8} />
        </BarChart>
      </ResponsiveContainer>
    </div>
    {/* Staff tip table */}
    <div style={{ background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <MIcon name="table_chart" size={18} color={T.t2} />
        <span style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Tips by Staff Member</span>
      </div>
      <DataTable defaultSort="tips" columns={[
        { key: "name", label: "Staff", align: "left", sortable: false, render: (v, r) => (<div><div style={{ fontSize: 12, fontWeight: 600, color: T.accent }}>{v}</div><div style={{ fontSize: 10, color: T.t3 }}>{r.role}</div></div>) },
        { key: "tips", label: "Total Tips", render: v => `$${v.toLocaleString()}` },
        { key: "hours", label: "Hours", render: v => v.toFixed(1) },
        { key: "tipPerHr", label: "Tip/Hr", render: v => <span style={{ color: T.green, fontWeight: 600 }}>${v}</span> },
        { key: "orders", label: "Orders", render: v => v },
      ]} rows={staffData.map(s => ({ ...s, tipPerHr: (s.tips / s.hours).toFixed(2) }))} />
    </div>
  </>);
}

function DrillVoids({ onBack }: any) {
  return (<>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
      <div onClick={onBack} style={{ width: 32, height: 32, borderRadius: 8, background: T.hover, border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <MIcon name="arrow_back" size={18} color={T.t2} />
      </div>
      <div>
        <div style={{ fontSize: 12, color: T.t3, fontFamily: T.font }}>Labor & Workforce</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, fontFamily: T.font }}>Void Detail Log</div>
      </div>
    </div>
    <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
      {[{ l: "TOTAL VOID $", v: "$365", i: "block", c: T.orange }, { l: "VOID ITEMS", v: "24", i: "inventory_2" }, { l: "VOID RATE", v: "1.2%", i: "percent" }].map(k => (
        <div key={k.l} style={{ flex: 1, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: "16px 20px", boxShadow: T.cardShadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: T.font }}>{k.l}</span>
            <MIcon name={k.i} size={18} color={T.t3} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, color: k.c || T.t1, fontFamily: T.font }}>{k.v}</div>
        </div>
      ))}
    </div>
    {/* Donut + progress bars */}
    <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
      <div style={{ flex: 1, background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
          <MIcon name="donut_large" size={18} color={T.t2} />
          <span style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Void Reasons</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <ResponsiveContainer width={140} height={140}><PieChart><Pie data={voidReasons} cx="50%" cy="50%" innerRadius={42} outerRadius={62} dataKey="value" strokeWidth={2} stroke={T.card}>{voidReasons.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie></PieChart></ResponsiveContainer>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
            {voidReasons.map(r => { const total = voidReasons.reduce((s, v) => s + v.value, 0); const pct = ((r.value / total) * 100).toFixed(0);
              return (<div key={r.name} style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flexShrink: 0 }} /><span style={{ fontSize: 11, color: T.t2, width: 100 }}>{r.name}</span><div style={{ flex: 1, height: 6, background: T.hover, borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, background: r.color, borderRadius: 3, opacity: 0.7 }} /></div><span style={{ fontSize: 11, fontWeight: 600, color: T.t1, width: 32, textAlign: "right" }}>{pct}%</span></div>);
            })}
          </div>
        </div>
      </div>
      {/* Voids by staff */}
      <div style={{ flex: 1, background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
          <MIcon name="person" size={18} color={T.t2} />
          <span style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Voids by Staff</span>
        </div>
        {staffData.filter(s => s.voids > 0).sort((a, b) => b.voids - a.voids).map(s => (
          <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.accent, width: 110, fontFamily: T.font }}>{s.name}</span>
            <div style={{ flex: 1, height: 6, background: T.hover, borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: `${(s.voids / 120) * 100}%`, background: T.orange, borderRadius: 3, opacity: 0.7 }} /></div>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.orange, width: 40, textAlign: "right", fontFamily: T.font }}>${s.voids}</span>
          </div>
        ))}
      </div>
    </div>
    {/* Full void log table */}
    <div style={{ background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <MIcon name="receipt_long" size={18} color={T.t2} />
        <span style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>All Void Entries</span>
      </div>
      <DataTable defaultSort="amount" columns={[
        { key: "date", label: "Date", align: "left", render: v => v },
        { key: "id", label: "ID", align: "left", sortable: false, render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
        { key: "staff", label: "Staff", align: "left", render: v => <span style={{ fontWeight: 600, color: T.accent }}>{v}</span> },
        { key: "item", label: "Item", align: "left", render: v => v },
        { key: "amount", label: "Amount", render: v => <span style={{ color: T.orange, fontWeight: 600 }}>${v}</span> },
        { key: "reason", label: "Reason", render: v => <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: T.hover, color: T.t2 }}>{v}</span> },
      ]} rows={voidLog} />
    </div>
  </>);
}

// ── Staff Leaderboard (main screen) ──
function StaffTable({ data }: any) {
  return (
    <div style={{ background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div><div style={{ fontSize: 14, fontWeight: 600, color: T.t1, fontFamily: T.font }}>Staff Efficiency Leaderboard</div><div style={{ fontSize: 11, color: T.t3, fontFamily: T.font, marginTop: 2 }}>Click headers to re-sort</div></div>
        <div style={{ padding: "4px 12px", borderRadius: 6, background: T.accentSoft, fontSize: 11, fontWeight: 500, color: T.accent, fontFamily: T.font, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}><MIcon name="download" size={14} color={T.accent} /> Export CSV</div>
      </div>
      <DataTable defaultSort="sales" columns={[
        { key: "name", label: "Staff Name", align: "left", sortable: false, render: (v, r) => (<div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: T.green, flexShrink: 0 }} /><div><div style={{ fontSize: 12, fontWeight: 600, color: T.accent }}>{v}</div><div style={{ fontSize: 10, color: T.t3, marginTop: 1 }}>{r.role}</div></div></div>) },
        { key: "hours", label: "Hours", render: v => v.toFixed(1) },
        { key: "sales", label: "Sales", render: v => `$${v.toLocaleString()}` },
        { key: "tips", label: "Tips", render: v => `$${v.toLocaleString()}` },
        { key: "orders", label: "Orders", render: v => v },
        { key: "splh", label: "SPLH", render: v => `$${v.toFixed(2)}` },
        { key: "voids", label: "Voids $", render: v => v > 0 ? <span style={{ color: T.orange }}>${v}</span> : "$0" },
        { key: "cancelRate", label: "Cancel %", render: v => { const c = v > 3 ? T.red : v > 1.5 ? T.orange : T.green; return <span style={{ color: c, fontWeight: 600 }}>{v.toFixed(1)}%</span>; } },
      ]} rows={data} />
    </div>
  );
}

// ── Main Screen Content ──
function MainContent({ onDrill }: any) {
  return (<>
    <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
      {kpiData.map(k => <KPICard key={k.label} {...k} onDrill={onDrill} />)}
    </div>
    <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
      <div style={{ flex: 2, background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}><MIcon name="stacked_line_chart" size={18} color={T.t2} /><span style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Hourly Labor vs. Sales Alignment</span></div>
        <div style={{ fontSize: 11, color: T.t3, marginBottom: 16, paddingLeft: 24 }}>Revenue bars · Staff count line</div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={hourlyData}><CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
            <XAxis dataKey="hour" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={{ stroke: T.divider }} tickLine={false} />
            <YAxis yAxisId="sales" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(1)}k`} />
            <YAxis yAxisId="staff" orientation="right" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} /><Bar yAxisId="sales" dataKey="sales" name="Sales" fill={T.accent} radius={[3, 3, 0, 0]} barSize={20} opacity={0.8} />
            <Line yAxisId="staff" type="monotone" dataKey="staff" name="Staff" stroke={T.green} strokeWidth={2.5} dot={{ r: 3, fill: T.green, stroke: "#fff", strokeWidth: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{ flex: 1, background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}><MIcon name="trending_up" size={18} color={T.t2} /><span style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>SPLH Trend</span></div>
        <div style={{ fontSize: 11, color: T.t3, marginBottom: 16, paddingLeft: 24 }}>Target: $35/hr</div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={splhTrend}><CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={{ stroke: T.divider }} tickLine={false} />
            <YAxis domain={[28, 50]} tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={35} stroke={T.t3} strokeDasharray="6 4" strokeWidth={1.5} label={{ value: "Target", position: "right", fontSize: 9, fill: T.t3 }} />
            <defs><linearGradient id="splhGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={T.accent} stopOpacity={0.15} /><stop offset="100%" stopColor={T.accent} stopOpacity={0.01} /></linearGradient></defs>
            <Area type="monotone" dataKey="splh" name="SPLH" stroke={T.accent} strokeWidth={2.5} fill="url(#splhGrad)" dot={{ r: 4, fill: T.accent, stroke: "#fff", strokeWidth: 2 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
    <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
      <div style={{ flex: 1, background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}><MIcon name="schedule" size={18} color={T.t2} /><span style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Daily Hours Breakdown</span></div>
        <div style={{ fontSize: 11, color: T.t3, marginBottom: 16, paddingLeft: 24 }}>Regular hours vs. break hours</div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={hoursBreakdown}><CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={{ stroke: T.divider }} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: T.t3, fontFamily: T.font }} axisLine={false} tickLine={false} /><Tooltip content={<CustomTooltip />} />
            <Bar dataKey="regular" name="Regular Hrs" stackId="hours" fill={T.accent} barSize={24} /><Bar dataKey="breaks" name="Break Hrs" stackId="hours" fill={T.t3} opacity={0.4} radius={[3, 3, 0, 0]} barSize={24} />
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", gap: 16, marginTop: 8, justifyContent: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.t2 }}><div style={{ width: 8, height: 8, borderRadius: 2, background: T.accent }} /> Regular</div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.t2 }}><div style={{ width: 8, height: 8, borderRadius: 2, background: T.t3, opacity: 0.4 }} /> Breaks</div>
        </div>
      </div>
      <div style={{ flex: 1, background: T.card, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}><MIcon name="block" size={18} color={T.t2} /><span style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>Void Reasons</span></div>
        <div style={{ fontSize: 11, color: T.t3, marginBottom: 16, paddingLeft: 24 }}>Distribution by category</div>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <ResponsiveContainer width={160} height={160}><PieChart><Pie data={voidReasons} cx="50%" cy="50%" innerRadius={48} outerRadius={68} dataKey="value" strokeWidth={2} stroke={T.card}>{voidReasons.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie></PieChart></ResponsiveContainer>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
            {voidReasons.map(r => { const total = voidReasons.reduce((s, v) => s + v.value, 0); const pct = ((r.value / total) * 100).toFixed(0);
              return (<div key={r.name} style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flexShrink: 0 }} /><span style={{ fontSize: 11, color: T.t2, width: 100 }}>{r.name}</span><div style={{ flex: 1, height: 6, background: T.hover, borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, background: r.color, borderRadius: 3, opacity: 0.7 }} /></div><span style={{ fontSize: 11, fontWeight: 600, color: T.t1, width: 32, textAlign: "right" }}>{pct}%</span></div>);
            })}
          </div>
        </div>
      </div>
    </div>
    <div style={{ marginBottom: 24 }}><StaffTable data={staffData} /></div>
    {/* Overtime Alert */}
    <div style={{ background: T.redSoft, border: `1px solid rgba(220,38,38,0.2)`, borderRadius: 14, padding: "16px 22px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><MIcon name="warning" size={18} color={T.red} fill /><span style={{ fontSize: 12, fontWeight: 600, color: T.red, fontFamily: T.font }}>Overtime Risk Alert</span></div>
      <div style={{ fontSize: 12, color: T.t2, lineHeight: 1.6, fontFamily: T.font }}><span style={{ fontWeight: 700, color: T.t1 }}>David Kim</span> has logged <span style={{ fontWeight: 700, color: T.t1 }}>40.0 hours</span> this week and is at the federal overtime threshold. Any additional hours will require 1.5× pay rate.</div>
    </div>
  </>);
}

// ── Root Component ──
export default function LaborReportDesktop() {
  const [activeReport, setActiveReport] = useState("Labor & Workforce");
  const [activeDatePreset, setActiveDatePreset] = useState("This Week");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [drillView, setDrillView] = useState<any>(null); // null | "hours" | "splh" | "tips" | "voids"

  const breadcrumbTitle = drillView === "hours" ? "Total Hours" : drillView === "splh" ? "SPLH Detail" : drillView === "tips" ? "Tip Breakdown" : drillView === "voids" ? "Void Log" : "Labor & Workforce";

  return (
    <div style={{ display: "flex", height: "100vh", background: T.bg, fontFamily: T.font, overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Sidebar */}
      <aside style={{ width: 250, minWidth: 250, height: "100vh", background: T.surface, borderRight: `1px solid ${T.divider}`, boxShadow: T.sidebarShadow, display: "flex", flexDirection: "column", position: "sticky", top: 0, overflowY: "auto" }}>
        <div style={{ padding: 20, borderBottom: `1px solid ${T.divider}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, #D23A3B, #E86B6B)", display: "flex", alignItems: "center", justifyContent: "center", color: "#FFF", fontSize: 16, fontWeight: 700 }}>M</div>
            <div><div style={{ fontSize: 15, fontWeight: 700, color: T.t1 }}>Maghil</div><div style={{ fontSize: 10, color: T.t3, letterSpacing: 0.5, textTransform: "uppercase" }}>Analytics</div></div>
          </div>
        </div>
        <div style={{ padding: "12px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: T.hover, border: `1px solid ${T.cardBorder}`, borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 600, color: T.t1 }}>
            <MIcon name="location_on" size={16} color={T.accent} fill /><span style={{ flex: 1 }}>Downtown Austin</span><ChevronSvg open={false} />
          </div>
        </div>
        <nav style={{ flex: 1, padding: "4px 8px", overflowY: "auto" }}>{navGroups.map(g => <SidebarNavGroup key={g.label} {...g} activeItem={activeReport} onSelect={setActiveReport} />)}</nav>
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.divider}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: T.accent }}>GM</div>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Alex Thompson</div><div style={{ fontSize: 10, color: T.t3 }}>General Manager</div></div>
        </div>
      </aside>

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* ── Top Bar: Breadcrumb + Export ── */}
        <header style={{ background: T.surface, borderBottom: `1px solid ${T.divider}`, position: "sticky", top: 0, zIndex: 10, boxShadow: T.topbarShadow }}>
          <div style={{ height: 48, display: "flex", alignItems: "center", padding: "0 24px", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: T.t3, fontFamily: T.font }}>Reports</span><MIcon name="chevron_right" size={14} color={T.t3} />
              <span style={{ fontSize: 12, color: T.t3 }}>Team</span><MIcon name="chevron_right" size={14} color={T.t3} />
              {drillView ? (<>
                <span style={{ fontSize: 12, color: T.t3, cursor: "pointer" }} onClick={() => setDrillView(null)}>Labor & Workforce</span>
                <MIcon name="chevron_right" size={14} color={T.t3} />
                <span style={{ fontSize: 14, fontWeight: 700, color: T.t1, fontFamily: T.font }}>{breadcrumbTitle}</span>
              </>) : (
                <span style={{ fontSize: 14, fontWeight: 700, color: T.t1, fontFamily: T.font }}>Labor & Workforce</span>
              )}
            </div>
            <button style={{ padding: "5px 14px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.surface, cursor: "pointer", fontSize: 11.5, color: T.t2, fontFamily: T.font, display: "flex", alignItems: "center", gap: 4 }}><MIcon name="download" size={16} color={T.t2} /> Export</button>
          </div>

          {/* ── Filter Bar ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px 10px" }}>
            {/* Date Presets */}
            <div style={{ display: "flex", alignItems: "center", gap: 4, padding: 3, background: T.bg, borderRadius: 8 }}>
              <MIcon name="calendar_today" size={15} color={T.t3} style={{ marginRight: 2, marginLeft: 6 }} />
              {datePresets.map(d => (
                <button key={d} onClick={() => setActiveDatePreset(d)} style={{
                  padding: "5px 14px", borderRadius: 6, border: "none", cursor: "pointer",
                  fontSize: 11.5, fontWeight: activeDatePreset === d ? 600 : 400, fontFamily: T.font,
                  background: activeDatePreset === d ? T.surface : "transparent",
                  color: activeDatePreset === d ? T.accent : T.t2,
                  boxShadow: activeDatePreset === d ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                  transition: "all 0.15s",
                }}>{d}</button>
              ))}
            </div>

            {/* Source Toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 4, padding: 3, background: T.bg, borderRadius: 8 }}>
              <MIcon name="filter_alt" size={15} color={T.t3} style={{ marginRight: 2, marginLeft: 6 }} />
              {["All", "Maghil", "Third Party"].map(s => (
                <button key={s} onClick={() => setSourceFilter(s)} style={{
                  padding: "5px 14px", borderRadius: 6, border: "none", cursor: "pointer",
                  fontSize: 11.5, fontWeight: sourceFilter === s ? 600 : 400, fontFamily: T.font,
                  background: sourceFilter === s ? T.surface : "transparent",
                  color: sourceFilter === s ? T.accent : T.t2,
                  boxShadow: sourceFilter === s ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                  transition: "all 0.15s",
                }}>{s}</button>
              ))}
            </div>
          </div>
        </header>
        <main style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {!drillView && <MainContent onDrill={setDrillView} />}
          {drillView === "hours" && <DrillHours onBack={() => setDrillView(null)} />}
          {drillView === "splh" && <DrillSplh onBack={() => setDrillView(null)} />}
          {drillView === "tips" && <DrillTips onBack={() => setDrillView(null)} />}
          {drillView === "voids" && <DrillVoids onBack={() => setDrillView(null)} />}
        </main>
      </div>
    </div>
  );
}
